import {
  SourceEntityMigrationType,
  type ISourceEntity,
  ISourceEntityTypes,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  ISODateTimeType,
  ISODateType,
  ISOTimeType,
  Timestamp,
  TypeGuard,
  isISODateTimeType,
  isISODateType,
  isISOTimeType,
  toISODate,
  toTimestamp,
  type Timezone,
} from '@principle-theorem/shared';
import { isBoolean, isNumber, isString } from 'lodash';
import * as moment from 'moment-timezone';
import { PATIENT_APPOINTMENT_RESOURCE_TYPE } from '../../../destination/entities/patient-appointments';
import { BaseSourceEntity } from '../../../source/base-source-entity';
import { SourceEntity } from '../../../source/source-entity';
import { OFFSET_PLACEHOLDER } from '../../../source/source-helpers';

export const PATIENT_APPOINTMENT_SOURCE_ENTITY: ISourceEntity =
  SourceEntity.init({
    metadata: {
      label: 'Patient Appointment List',
      description: '',
      idPrefix: PATIENT_APPOINTMENT_RESOURCE_TYPE,
      migrationType: SourceEntityMigrationType.Automatic,
    },
  });

export interface IOasisPatientAppointment {
  id: string;
  patientId: number;
  practitionerId: number;
  appointmentPractitionerId: number;
  treatmentStepId?: number;
  date: ISODateType;
  time: ISOTimeType;
  from: ISODateTimeType;
  performedAt?: ISODateTimeType;
  // raw OASiS slot count (multiply by step size for minutes)
  duration: number;
  createdAt?: ISODateTimeType;
  createdBy: string;
  categoryCode: string;
  referenceId?: number;
  isConfirmed: boolean;
  amount?: number;
  updatedAt?: ISODateTimeType;
  treatmentSummary?: string;
  notes?: string;
}

export function isOasisPatientAppointment(
  item: unknown,
  logError: boolean
): item is IOasisPatientAppointment {
  return TypeGuard.interface<IOasisPatientAppointment>(
    {
      id: isString,
      patientId: isNumber,
      practitionerId: isNumber,
      appointmentPractitionerId: isNumber,
      treatmentStepId: TypeGuard.nilOr(isNumber),
      date: isISODateType,
      time: isISOTimeType,
      from: isISODateTimeType,
      performedAt: TypeGuard.nilOr(isISODateTimeType),
      duration: isNumber,
      createdAt: TypeGuard.nilOr(isISODateTimeType),
      createdBy: isString,
      categoryCode: isString,
      referenceId: TypeGuard.nilOr(isNumber),
      isConfirmed: isBoolean,
      amount: TypeGuard.nilOr(isNumber),
      updatedAt: TypeGuard.nilOr(isISODateTimeType),
      treatmentSummary: TypeGuard.nilOr(isString),
      notes: TypeGuard.nilOr(isString),
    },
    logError
  )(item);
}

export interface IOasisPatientAppointmentTranslations {
  createdAt: Timestamp;
  from: Timestamp;
}

export interface IOasisPatientAppointmentFilters {
  practitionerId?: number;
  patientId: number;
  from: Timestamp;
  date: ISODateType;
}

const PATIENT_APPOINTMENT_SOURCE_QUERY = `
SELECT room.performed_at, appointment.* FROM (
	SELECT
		convert_to_text(SKEY) AS id,
		convert_to_integer(PATNUMBER) AS patient_id,
		DRNUMBER AS practitioner_id,
		NULLIF(REFNUMBER, 0) as treatment_step_id,
		REGEXP_REPLACE(SKEY,'([0-9]{4})([0-9]{2})([0-9]{2})(.*)','\\1-\\2-\\3') AS date,
    convert_to_integer(REGEXP_REPLACE(SKEY,'([0-9]{9})([0-9]{7})(.*)','\\2')) AS appointment_practitioner_id,
		to_char(
      NULLIF(CONCAT(
			  REGEXP_REPLACE(SKEY,'([0-9]{16})([0-9]{2})(.*)','\\2'),
			  ':',
			  RPAD((convert_to_integer(REGEXP_REPLACE(SKEY,'([0-9]{18})([0-9]{2})(.*)','\\2'))* 5 - 5)::TEXT, 2, '0')
		  ), '')::TIME,
      'HH24:MI:SS'
    ) AS time,
    to_char(
      convert_to_date(REGEXP_REPLACE(SKEY,'([0-9]{4})([0-9]{2})([0-9]{2})(.*)','\\1-\\2-\\3'), 'YYYY-MM-DD')
      +
      NULLIF(CONCAT(
        REGEXP_REPLACE(SKEY,'([0-9]{16})([0-9]{2})(.*)','\\2'),
        ':',
        RPAD((convert_to_integer(REGEXP_REPLACE(SKEY,'([0-9]{18})([0-9]{2})(.*)','\\2'))* 5 - 5)::TEXT, 2, '0')
      ), '')::TIME,
      'YYYY-MM-DD HH24:MI:SS'
    ) AS from,
		TIMEUSED AS duration,
    to_char(
      to_date(normalise_oasis_date_field(ENTRYDATE), 'YYYY-MM-DD')
      +
      CASE
        WHEN ENTRYTIME ~ '^\\d{1,2}:\\d{2}:\\d{2} (AM|PM)$'
          THEN NULLIF(TRIM(ENTRYTIME), '')::TIME
        WHEN ENTRYTIME ~ '^\\d{2}:\\d{2}:\\d{2}$'
          THEN NULLIF(TRIM(ENTRYTIME), '')::TIME
        ELSE NULL
      END,
      'YYYY-MM-DD HH24:MI:SS'
    ) AS created_at,
		ENTRYUSER AS created_by,
		SORTCODE AS category_code,
		NULLIF(convert_to_integer(AMOUNT), 0) AS amount,
		CASE WHEN CONFIRMEDCODE = 'C'
			THEN TRUE
			ELSE FALSE
		  END AS is_confirmed,
    CASE
      WHEN LASTUPDATE ~ '^\\d{4}-\\d{2}-\\d{2} \\d{2}:\\d{2}:\\d{2}$' THEN
        to_char(to_timestamp(LASTUPDATE, 'YYYY-MM-DD HH24:MI:SS'), 'YYYY-MM-DD HH24:MI:SS')
      WHEN LASTUPDATE ~ '^\\d{2}/\\d{2}/\\d{4} \\d{1,2}:\\d{2}:\\d{2} (AM|PM)$' THEN
        to_char(to_timestamp(LASTUPDATE, 'DD/MM/YYYY HH:MI:SS AM'), 'YYYY-MM-DD HH24:MI:SS')
      WHEN LASTUPDATE ~ '^\\d{2}-\\d{2}-\\d{4} \\d{1,2}:\\d{2}:\\d{2} (AM|PM)$' THEN
        to_char(to_timestamp(LASTUPDATE, 'DD-MM-YYYY HH:MI:SS AM'), 'YYYY-MM-DD HH24:MI:SS')
    END AS updated_at,
		details AS treatment_summary,
		NULLIF(REPLACE(NOTES, '��', '\r\n'), '') AS notes
	FROM PAAPPLNS
	WHERE
		SKEY NOT LIKE '9999%'
		AND NULLIF(convert_to_integer(PATNUMBER), 0) IS NOT NULL
    AND to_char(
      NULLIF(
        CONCAT(
          REGEXP_REPLACE(SKEY,'([0-9]{16})([0-9]{2})(.*)','\\2'),
          ':',
          RPAD((convert_to_integer(REGEXP_REPLACE(SKEY,'([0-9]{18})([0-9]{2})(.*)','\\2'))* 5 - 5)::TEXT, 2, '0')
        ),
        ''
      )::TIME,
      'HH24:MI:SS'
    ) >= '06:00:00'
  ORDER BY SKEY
  ${OFFSET_PLACEHOLDER}
) AS appointment
LEFT JOIN (
	SELECT
		SKEY::TEXT AS id,
		convert_to_integer(PATNUMBER) AS patient_id,
		DRNUMBER AS practitioner_id,
		to_char(
      convert_to_date(REGEXP_REPLACE(SKEY::TEXT,'([0-9]{4})([0-9]{2})([0-9]{2})(.*)','\\1-\\2-\\3'), 'YYYY-MM-DD')
      +
      NULLIF(APPTIME, '')::TIME
      ,
      'YYYY-MM-DD HH:MI:SS'
  ) AS performed_at
	FROM PAWTROOS
) AS room
ON
	room.patient_id = appointment.patient_id
	AND room.practitioner_id = appointment.practitioner_id
	AND room.performed_at = appointment.from
`;

const PATIENT_APPOINTMENT_SOURCE_QUERY_V2 = `
SELECT room.performed_at, appointment.* FROM (
	SELECT
		convert_to_text(SKEY) AS id,
		convert_to_integer(PATNUMBER) AS patient_id,
		DRNUMBER AS practitioner_id,
		NULLIF(REFNUMBER, 0) as treatment_step_id,
		REGEXP_REPLACE(SKEY,'([0-9]{4})([0-9]{2})([0-9]{2})(.*)','\\1-\\2-\\3') AS date,
    convert_to_integer(REGEXP_REPLACE(SKEY,'([0-9]{9})([0-9]{7})(.*)','\\2')) AS appointment_practitioner_id,
		to_char(
      NULLIF(CONCAT(
			  REGEXP_REPLACE(SKEY,'([0-9]{16})([0-9]{2})(.*)','\\2'),
			  ':',
			  RPAD((convert_to_integer(REGEXP_REPLACE(SKEY,'([0-9]{18})([0-9]{2})(.*)','\\2'))* 5 - 5)::TEXT, 2, '0')
		  ), '')::TIME,
      'HH24:MI:SS'
    ) AS time,
    to_char(
      convert_to_date(REGEXP_REPLACE(SKEY,'([0-9]{4})([0-9]{2})([0-9]{2})(.*)','\\1-\\2-\\3'), 'YYYY-MM-DD')
      +
      NULLIF(CONCAT(
        REGEXP_REPLACE(SKEY,'([0-9]{16})([0-9]{2})(.*)','\\2'),
        ':',
        RPAD((convert_to_integer(REGEXP_REPLACE(SKEY,'([0-9]{18})([0-9]{2})(.*)','\\2'))* 5 - 5)::TEXT, 2, '0')
      ), '')::TIME,
      'YYYY-MM-DD HH24:MI:SS'
    ) AS from,
		TIMEUSED AS duration,
    to_char(
      to_date(normalise_oasis_date_field(ENTRYDATE), 'YYYY-MM-DD')
      +
      CASE
        WHEN ENTRYTIME ~ '^\\d{1,2}:\\d{2}:\\d{2} (AM|PM)$'
          THEN NULLIF(TRIM(ENTRYTIME), '')::TIME
        WHEN ENTRYTIME ~ '^\\d{2}:\\d{2}:\\d{2}$'
          THEN NULLIF(TRIM(ENTRYTIME), '')::TIME
        ELSE NULL
      END,
      'YYYY-MM-DD HH24:MI:SS'
    ) AS created_at,
		ENTRYUSER AS created_by,
		SORTCODE AS category_code,
		NULLIF(convert_to_integer(AMOUNT), 0) AS amount,
		COALESCE(CONFIRMEDCODE, false) AS is_confirmed,
    CASE
      WHEN LASTUPDATE ~ '^\\d{4}-\\d{2}-\\d{2} \\d{2}:\\d{2}:\\d{2}$' THEN
        to_char(to_timestamp(LASTUPDATE, 'YYYY-MM-DD HH24:MI:SS'), 'YYYY-MM-DD HH24:MI:SS')
      WHEN LASTUPDATE ~ '^\\d{2}/\\d{2}/\\d{4} \\d{1,2}:\\d{2}:\\d{2} (AM|PM)$' THEN
        to_char(to_timestamp(LASTUPDATE, 'DD/MM/YYYY HH:MI:SS AM'), 'YYYY-MM-DD HH24:MI:SS')
      WHEN LASTUPDATE ~ '^\\d{2}-\\d{2}-\\d{4} \\d{1,2}:\\d{2}:\\d{2} (AM|PM)$' THEN
        to_char(to_timestamp(LASTUPDATE, 'DD-MM-YYYY HH:MI:SS AM'), 'YYYY-MM-DD HH24:MI:SS')
    END AS updated_at,
		details AS treatment_summary,
		NULLIF(REPLACE(NOTES, '��', '\r\n'), '') AS notes
	FROM PAAPPLNS
	WHERE
		SKEY NOT LIKE '9999%'
		AND NULLIF(convert_to_integer(PATNUMBER), 0) IS NOT NULL
    AND to_char(
      NULLIF(
        CONCAT(
          REGEXP_REPLACE(SKEY,'([0-9]{16})([0-9]{2})(.*)','\\2'),
          ':',
          RPAD((convert_to_integer(REGEXP_REPLACE(SKEY,'([0-9]{18})([0-9]{2})(.*)','\\2'))* 5 - 5)::TEXT, 2, '0')
        ),
        ''
      )::TIME,
      'HH24:MI:SS'
    ) >= '06:00:00'
  ORDER BY SKEY
  ${OFFSET_PLACEHOLDER}
) AS appointment
LEFT JOIN (
	SELECT
		SKEY::TEXT AS id,
		convert_to_integer(PATNUMBER) AS patient_id,
		DRNUMBER AS practitioner_id,
		to_char(
      convert_to_date(REGEXP_REPLACE(SKEY::TEXT,'([0-9]{4})([0-9]{2})([0-9]{2})(.*)','\\1-\\2-\\3'), 'YYYY-MM-DD')
      +
      NULLIF(APPTIME, '')::TIME
      ,
      'YYYY-MM-DD HH:MI:SS'
  ) AS performed_at
	FROM PAWTROOS
) AS room
ON
	room.patient_id = appointment.patient_id
	AND room.practitioner_id = appointment.practitioner_id
	AND room.performed_at = appointment.from
`;

const PATIENT_APPOINTMENT_SOURCE_QUERY_V3 = `
SELECT room.performed_at, appointment.* FROM (
	SELECT
		convert_to_text(SKEY) AS id,
		PATNUMBER AS patient_id,
		DRNUMBER AS practitioner_id,
		NULLIF(REFNUMBER, 0) as treatment_step_id,
		REGEXP_REPLACE(SKEY::TEXT,'([0-9]{4})([0-9]{2})([0-9]{2})(.*)','\\1-\\2-\\3') AS date,
    convert_to_integer(REGEXP_REPLACE(SKEY::TEXT,'([0-9]{10})([0-9]{2})(.*)','\\2')) AS appointment_practitioner_id,
		to_char(
      NULLIF(CONCAT(
        COALESCE(REGEXP_REPLACE(SKEY::TEXT,'([0-9]{12})([0-9]{2})(.*)','\\2'), '00'),
        ':',
        RPAD(GREATEST(0, convert_to_integer(REGEXP_REPLACE(SKEY::TEXT,'([0-9]{14})([0-9]{2})(.*)','\\2'))* 5 - 5)::TEXT, 2, '0')
      ), '')::TIME,
      'HH24:MI:SS'
    ) AS time,
    to_char(
      convert_to_date(REGEXP_REPLACE(SKEY::TEXT,'([0-9]{4})([0-9]{2})([0-9]{2})(.*)','\\1-\\2-\\3'), 'YYYY-MM-DD')
      +
      NULLIF(CONCAT(
        COALESCE(REGEXP_REPLACE(SKEY::TEXT,'([0-9]{12})([0-9]{2})(.*)','\\2'), '00'),
        ':',
        RPAD(GREATEST(0, convert_to_integer(REGEXP_REPLACE(SKEY::TEXT,'([0-9]{14})([0-9]{2})(.*)','\\2'))* 5 - 5)::TEXT, 2, '0')
      ), '')::TIME,
      'YYYY-MM-DD HH24:MI:SS'
    ) AS from,
		TIMEUSED AS duration,
    to_char(
      to_date(normalise_oasis_date_field(ENTRYDATE), 'YYYY-MM-DD')
      +
      CASE
        WHEN ENTRYTIME ~ '^\\d{1,2}:\\d{2}:\\d{2} (AM|PM)$'
          THEN NULLIF(TRIM(ENTRYTIME), '')::TIME
        WHEN ENTRYTIME ~ '^\\d{2}:\\d{2}:\\d{2}$'
          THEN NULLIF(TRIM(ENTRYTIME), '')::TIME
        ELSE NULL
      END,
      'YYYY-MM-DD HH24:MI:SS'
    ) AS created_at,
		ENTRYUSER AS created_by,
		SORTCODE AS category_code,
		NULLIF(convert_to_integer(AMOUNT), 0) AS amount,
		CASE WHEN CONFIRMED = 1
			THEN TRUE
			ELSE FALSE
		  END AS is_confirmed,
		details AS treatment_summary,
		NULLIF(REPLACE(NOTES, '��', '\r\n'), '') AS notes
	FROM PAAPPLNS
	WHERE
		SKEY::TEXT NOT LIKE '9999%'
		AND PATNUMBER > 0
    AND to_char(
      NULLIF(CONCAT(
        COALESCE(REGEXP_REPLACE(SKEY::TEXT,'([0-9]{12})([0-9]{2})(.*)','\\2'), '00'),
        ':',
        RPAD(GREATEST(0, convert_to_integer(REGEXP_REPLACE(SKEY::TEXT,'([0-9]{14})([0-9]{2})(.*)','\\2'))* 5 - 5)::TEXT, 2, '0')
      ), '')::TIME,
      'HH24:MI:SS'
    ) >= '06:00:00'
  ORDER BY SKEY
  ${OFFSET_PLACEHOLDER}
) AS appointment
LEFT JOIN (
	SELECT
		SKEY::TEXT AS id,
		convert_to_integer(PATNUMBER) AS patient_id,
		DRNUMBER AS practitioner_id,
		to_char(
      convert_to_date(REGEXP_REPLACE(SKEY::TEXT,'([0-9]{4})([0-9]{2})([0-9]{2})(.*)','\\1-\\2-\\3'), 'YYYY-MM-DD')
      +
      NULLIF(APPTIME, '')::TIME
      ,
      'YYYY-MM-DD HH:MI:SS'
  ) AS performed_at
	FROM PAWTROOS
) AS room
ON
	room.patient_id = appointment.patient_id
	AND room.practitioner_id = appointment.practitioner_id
	AND room.performed_at = appointment.from
`;

/**
 * Oasis v8 variant: SKEY is a BIGINT (16 digits) rather than v9 text, and
 * PAAPPLNS has no ENTRYDATE/ENTRYTIME/LASTUPDATE/CONFIRMEDCODE columns. Instead
 * it exposes a single combined ENTRYDATETIME field. This query casts SKEY to
 * text everywhere, derives created_at from ENTRYDATETIME (which appears with
 * both '-' and '/' date separators), and drops the missing columns.
 */
const PATIENT_APPOINTMENT_SOURCE_QUERY_V4 = `
SELECT room.performed_at, appointment.* FROM (
	SELECT
		convert_to_text(SKEY) AS id,
		PATNUMBER AS patient_id,
		DRNUMBER AS practitioner_id,
		NULLIF(REFNUMBER, 0) as treatment_step_id,
		REGEXP_REPLACE(SKEY::TEXT,'([0-9]{4})([0-9]{2})([0-9]{2})(.*)','\\1-\\2-\\3') AS date,
    convert_to_integer(REGEXP_REPLACE(SKEY::TEXT,'([0-9]{10})([0-9]{2})(.*)','\\2')) AS appointment_practitioner_id,
		to_char(
      NULLIF(CONCAT(
        COALESCE(REGEXP_REPLACE(SKEY::TEXT,'([0-9]{12})([0-9]{2})(.*)','\\2'), '00'),
        ':',
        RPAD(GREATEST(0, convert_to_integer(REGEXP_REPLACE(SKEY::TEXT,'([0-9]{14})([0-9]{2})(.*)','\\2'))* 5 - 5)::TEXT, 2, '0')
      ), '')::TIME,
      'HH24:MI:SS'
    ) AS time,
    to_char(
      convert_to_date(REGEXP_REPLACE(SKEY::TEXT,'([0-9]{4})([0-9]{2})([0-9]{2})(.*)','\\1-\\2-\\3'), 'YYYY-MM-DD')
      +
      NULLIF(CONCAT(
        COALESCE(REGEXP_REPLACE(SKEY::TEXT,'([0-9]{12})([0-9]{2})(.*)','\\2'), '00'),
        ':',
        RPAD(GREATEST(0, convert_to_integer(REGEXP_REPLACE(SKEY::TEXT,'([0-9]{14})([0-9]{2})(.*)','\\2'))* 5 - 5)::TEXT, 2, '0')
      ), '')::TIME,
      'YYYY-MM-DD HH24:MI:SS'
    ) AS from,
		TIMEUSED AS duration,
    CASE
      WHEN ENTRYDATETIME ~ '^\\d{2}-\\d{2}-\\d{4} \\d{1,2}:\\d{2}:\\d{2} (AM|PM)$' THEN
        to_char(to_timestamp(ENTRYDATETIME, 'DD-MM-YYYY HH:MI:SS AM'), 'YYYY-MM-DD HH24:MI:SS')
      WHEN ENTRYDATETIME ~ '^\\d{2}/\\d{2}/\\d{4} \\d{1,2}:\\d{2}:\\d{2} (AM|PM)$' THEN
        to_char(to_timestamp(ENTRYDATETIME, 'DD/MM/YYYY HH:MI:SS AM'), 'YYYY-MM-DD HH24:MI:SS')
      ELSE NULL
    END AS created_at,
    null AS updated_at,
		ENTRYUSER AS created_by,
		COLORNUMBER AS category_id,
		SORTCODE AS category_code,
		NULLIF(convert_to_integer(AMOUNT), 0) AS amount,
		CASE WHEN CONFIRMED = 1
			THEN TRUE
			ELSE FALSE
		  END AS is_confirmed,
		details AS treatment_summary,
		NULLIF(REPLACE(NOTES, '��', '\r\n'), '') AS notes
	FROM PAAPPLNS
	WHERE
		SKEY::TEXT NOT LIKE '9999%'
		AND PATNUMBER > 0
    AND to_char(
      NULLIF(CONCAT(
        COALESCE(REGEXP_REPLACE(SKEY::TEXT,'([0-9]{12})([0-9]{2})(.*)','\\2'), '00'),
        ':',
        RPAD(GREATEST(0, convert_to_integer(REGEXP_REPLACE(SKEY::TEXT,'([0-9]{14})([0-9]{2})(.*)','\\2'))* 5 - 5)::TEXT, 2, '0')
      ), '')::TIME,
      'HH24:MI:SS'
    ) >= '06:00:00'
  ORDER BY SKEY
  ${OFFSET_PLACEHOLDER}
) AS appointment
LEFT JOIN (
	SELECT
		SKEY::TEXT AS id,
		convert_to_integer(PATNUMBER) AS patient_id,
		DRNUMBER AS practitioner_id,
		to_char(
      convert_to_date(REGEXP_REPLACE(SKEY::TEXT,'([0-9]{4})([0-9]{2})([0-9]{2})(.*)','\\1-\\2-\\3'), 'YYYY-MM-DD')
      +
      NULLIF(APPTIME, '')::TIME
      ,
      'YYYY-MM-DD HH:MI:SS'
  ) AS performed_at
	FROM PAWTROOS
) AS room
ON
	room.patient_id = appointment.patient_id
	AND room.practitioner_id = appointment.practitioner_id
	AND room.performed_at = appointment.from
`;

const PATIENT_APPOINTMENT_ESTIMATE_QUERY = `
SELECT
	SKEY AS id
FROM PAAPPLNS
WHERE
	SKEY NOT LIKE '9999%'
	AND NULLIF(convert_to_integer(PATNUMBER), 0) IS NOT NULL
  AND to_char(
    NULLIF(
      CONCAT(
        REGEXP_REPLACE(SKEY,'([0-9]{16})([0-9]{2})(.*)','\\2'),
        ':',
        RPAD((convert_to_integer(REGEXP_REPLACE(SKEY,'([0-9]{18})([0-9]{2})(.*)','\\2'))* 5 - 5)::TEXT, 2, '0')
      ),
      ''
    )::TIME,
    'HH24:MI:SS'
  ) >= '06:00:00'
ORDER BY SKEY
`;

const PATIENT_APPOINTMENT_ESTIMATE_QUERY_V2 = `
SELECT
	SKEY::TEXT AS id
FROM PAAPPLNS
WHERE
	SKEY::TEXT NOT LIKE '9999%'
	AND PATNUMBER > 0
  AND to_char(
    NULLIF(CONCAT(
      COALESCE(REGEXP_REPLACE(SKEY::TEXT,'([0-9]{12})([0-9]{2})(.*)','\\2'), '00'),
      ':',
      RPAD(GREATEST(0, convert_to_integer(REGEXP_REPLACE(SKEY::TEXT,'([0-9]{14})([0-9]{2})(.*)','\\2'))* 5 - 5)::TEXT, 2, '0')
    ), '')::TIME,
    'HH24:MI:SS'
  ) >= '06:00:00'
ORDER BY SKEY
`;

export type OasisPatientAppointment = ISourceEntityTypes<
  IOasisPatientAppointment,
  IOasisPatientAppointmentTranslations,
  IOasisPatientAppointmentFilters
>;

export class PatientAppointmentSourceEntity extends BaseSourceEntity<OasisPatientAppointment> {
  sourceEntity = PATIENT_APPOINTMENT_SOURCE_ENTITY;
  entityResourceType = PATIENT_APPOINTMENT_RESOURCE_TYPE;
  sourceQuery = [
    PATIENT_APPOINTMENT_SOURCE_QUERY,
    PATIENT_APPOINTMENT_SOURCE_QUERY_V2,
    PATIENT_APPOINTMENT_SOURCE_QUERY_V4,
  ];
  verifySourceFn = isOasisPatientAppointment;
  allowOffsetJob = true;
  override estimateQuery = [
    PATIENT_APPOINTMENT_ESTIMATE_QUERY,
    PATIENT_APPOINTMENT_ESTIMATE_QUERY_V2,
    PATIENT_APPOINTMENT_SOURCE_QUERY_V3,
  ];

  translate(
    data: IOasisPatientAppointment,
    timezone: Timezone
  ): IOasisPatientAppointmentTranslations {
    return {
      createdAt: toTimestamp(moment.tz(data.createdAt ?? data.from, timezone)),
      from: toTimestamp(moment.tz(data.from, timezone)),
    };
  }

  getSourceRecordId(data: IOasisPatientAppointment): string {
    return data.id;
  }

  getSourceLabel(data: IOasisPatientAppointment): string {
    return data.id.toString();
  }

  getFilterData(
    data: IOasisPatientAppointment,
    timezone: Timezone
  ): IOasisPatientAppointmentFilters {
    return {
      practitionerId: data.practitionerId,
      patientId: data.patientId,
      from: toTimestamp(moment.tz(data.from, timezone)),
      date: toISODate(moment.tz(data.from, timezone)),
    };
  }
}
